import createShakaHeuristic                from 'playback-web-shaka-heuristic';
import IShakaHeuristic                     from 'playback-web-shaka-heuristic/dist/Init/Interfaces/IContext';
import {Observable, Subject, Subscription} from 'rxjs';
import * as shaka                          from 'shaka-player';

import IErrorsPayload                     from '../../Player/Interfaces/IErrorsPayload';
import IPlayer                            from '../../Player/Interfaces/IPlayer';
import Track                              from '../../State/Stores/Captions/Track';
import Media                              from '../../State/Stores/Media/Media';
import Html5Base                          from '../Shared/Abstract/Html5Base';
import DrmType                            from '../Shared/Constants/DrmType';
import RobustnessLevel                    from '../Shared/Constants/RobustnessLevel';
import SolutionType                       from '../Shared/Constants/SolutionType';
import SyntheticMediaEventType            from '../Shared/Constants/SyntheticMediaEventType';
import {createShakaPlayreadyDrmModifiers} from '../Shared/DrmModifiers/createPlayreadyDrmModifiers';
import {createShakaWidevineDrmModifiers}  from '../Shared/DrmModifiers/createWidevineDrmModifiers';
import IMediaEventsStreamPayload          from '../Shared/Interfaces/IMediaEventsStreamPayload';
import ISolutionState                     from '../Shared/Interfaces/ISolutionState';
import createMediaEventsStream            from '../Shared/Streams/createMediaEventsStream';
import inferDrmTypeFromSrc                from '../Shared/Util/inferDrmTypeFromSrc';
import mapLanguageCodeToLabel             from '../Shared/Util/mapLanguageCodeToLabel';
import normaliseLanguageCode              from '../Shared/Util/normaliseLanguageCode';

import connectSolutionSubscriptions from './connectSolutionSubscriptions';
import createMediaErrorsStream      from './Streams/createMediaErrorsStream';

const {RequestType} = shaka.net.NetworkingEngine;
const REBUFFER_TIMEOUT_MS = 500;

class Shaka extends Html5Base {
    public type: SolutionType;
    public version: string;
    public mediaEvents$: Observable<IMediaEventsStreamPayload>;
    public mediaErrors$: Observable<IErrorsPayload>;
    public videoElement: HTMLVideoElement;
    protected subscriptions: Subscription[];
    protected syntheticMediaEvents$: Subject<SyntheticMediaEventType>;
    private loadErrors$ = new Subject<Error>();
    private activeTextTrack: TextTrack = null;
    private player: IPlayer;
    private shakaPlayer: any;
    private shakaHeuristic: IShakaHeuristic;

    constructor(
        player: IPlayer,
        type: SolutionType,
        injectedShakaPlayer = shaka.Player,
        injectedCreateShakaHeuristic = createShakaHeuristic
    ) {
        super();

        const {stores} = player.state;

        this.player = player;
        this.type = type;
        this.shakaPlayer = Shaka.createShakaPlayer(this.player.refs.solution, injectedShakaPlayer);
        this.version = shaka.Player.version;
        this.videoElement = this.shakaPlayer.getMediaElement();
        this.mediaErrors$ = createMediaErrorsStream(this.shakaPlayer, this.loadErrors$, stores.playback.update$);
        this.mediaEvents$ = createMediaEventsStream(stores, this, this.syntheticMediaEvents$);
        this.subscriptions = connectSolutionSubscriptions(stores, this);
        this.handleCueChange = this.handleCueChange.bind(this);

        this.shakaHeuristic = injectedCreateShakaHeuristic({
            videoElement: this.videoElement,
            player: this.shakaPlayer,
            baseConfig: {}
        });

        const lastEstimatedBandwidth = this.player.state.stores.localStorage.value.estimatedBandwidth;

        if (typeof lastEstimatedBandwidth === 'number' && lastEstimatedBandwidth > 0) {
            this.shakaHeuristic.setBandwidthEstimate(lastEstimatedBandwidth);
        }
}

    public get state(): ISolutionState {
        const {start, end} = this.shakaPlayer.seekRange();
        const {currentTime, volume} = this.videoElement;
        const {width, height, estimatedBandwidth} = this.shakaPlayer.getStats();
        const activeCues = this.activeTextTrack && this.activeTextTrack.activeCues ?
            Array.from(this.activeTextTrack.activeCues).map(cue => ({text: cue.text})) :
            [];

        return {
            minTime: start,
            maxTime: end,
            currentTime,
            resolution: `${width}x${height}`,
            volume,
            activeCues,
            estimatedBandwidth
        };
    }

    public get textTracks(): Track[] {
        const textTracks = this.shakaPlayer.getTextTracks();

        return textTracks.map((shakaTrack, index) => {
            const track = new Track();

            track.index = index;
            track.language = normaliseLanguageCode(shakaTrack.language);
            track.label = mapLanguageCodeToLabel(track.language);

            return track;
        });
    }

    public async load(media: Media): Promise<void> {
        const {isEncrypted, src, startTime} = media;

        try {
            if (isEncrypted) {
                await this.configureContentProtection(media);
            }

            const resumeFrom = startTime > 0 ? startTime : null;

            this.rebuffer();

            await this.shakaPlayer.load(src, resumeFrom);

            this.syntheticMediaEvents$.next(SyntheticMediaEventType.READY);
        } catch (err) {
            this.loadErrors$.next(err);
        }
    }

    public unload(): Promise<void> {
        return this.shakaPlayer.unload();
    }

    public seek(nextCurrentTime: number): void {
        this.rebuffer();
        this.videoElement.currentTime = nextCurrentTime;
    }

    public async getBitrate(): Promise<number> {
        const variantTracks = this.shakaPlayer.getVariantTracks();
        const activeTrack = variantTracks.find(track => track.active);

        return activeTrack ? activeTrack.videoBandwidth : 0;
    }

    public setActiveTextTrack(index: number): void {
        const shakaTextTracks = this.shakaPlayer.getTextTracks();
        const textTrack = this.videoElement.textTracks[0] || null;

        if (index > -1) {
            this.shakaPlayer.selectTextTrack(shakaTextTracks[index]);
            this.shakaPlayer.setTextTrackVisibility(true);

            textTrack.mode = 'hidden';
            textTrack.addEventListener('cuechange', this.handleCueChange);
        } else if (textTrack) {
            this.shakaPlayer.setTextTrackVisibility(false);

            textTrack.mode = 'disabled';
            textTrack.removeEventListener('cuechange', this.handleCueChange);
        }

        this.activeTextTrack = textTrack;
        this.handleCueChange();
    }

    public async destroy(): Promise<void> {
        while (this.subscriptions.length > 0) this.subscriptions.pop().unsubscribe();

        this.shakaHeuristic.destroy();

        await this.shakaPlayer.destroy();
    }

    /*
    ** Set Heuristic into REBUFFER state
    ** see https://github.com/getndazn/playback-web-shaka-heuristic/pull/30
    */
    private rebuffer(): void {
        const {STATE_REBUFFER} = this.shakaHeuristic;
        this.shakaHeuristic.setState(STATE_REBUFFER, REBUFFER_TIMEOUT_MS);
    }

    private configureContentProtection(media: Media): void {
        const {config} = this.player.state.stores;
        const drmType: DrmType = inferDrmTypeFromSrc(media.src);
        const {licenseUrl, releasePid, authToken} = config.value.drm;
        const networkingEngine = this.shakaPlayer.getNetworkingEngine();

        this.shakaPlayer.configure({
            drm: {
                servers: {
                    'com.widevine.alpha': licenseUrl,
                    'com.microsoft.playready': licenseUrl
                },
                advanced: {
                    'com.widevine.alpha': {
                        videoRobustness: RobustnessLevel.SW_SECURE_CRYPTO
                    }
                }
            }
        });

        switch (drmType) {
            case DrmType.WIDEVINE:
                const widevineModifiers = createShakaWidevineDrmModifiers(
                    RequestType.LICENSE,
                    releasePid,
                    authToken
                );

                networkingEngine.registerRequestFilter(widevineModifiers.licenseRequest);
                networkingEngine.registerResponseFilter(widevineModifiers.licenseResponse);

                break;
            case DrmType.PLAYREADY:
                const playreadyModifiers = createShakaPlayreadyDrmModifiers(
                    RequestType.LICENSE,
                    authToken
                );

                networkingEngine.registerRequestFilter(playreadyModifiers.licenseRequest);

                break;
        }
    }

    private handleCueChange(): void {
        this.syntheticMediaEvents$.next(SyntheticMediaEventType.CUE_CHANGE);
    }

    private static createShakaPlayer(solutionElement: HTMLElement, ShakaPlayer: any): shaka['Player'] {
        const videoElement = document.createElement('video');

        solutionElement.appendChild(videoElement);

        const shakaPlayer = new ShakaPlayer(videoElement);

        return shakaPlayer;
    }
}

export default Shaka;